import { useState, useMemo } from 'react';
import { type SuburbType } from '@discover-data/gql/fragments/suburb';
import useSuburbs from '@discover-data/stores/hooks/suburb/useSuburbs';
import CheckboxGroup from '@discover-ui/checkboxGroup';
import Input from '@discover-ui/input';
import SearchIcon from '@discover-ui/icons/search.svg';

import FilterAccordionItem from '../filterAccordionItem';
import useExpandedFilterOptions, { COLLAPSED_FILTER_LIMIT } from '../useExpandedFilterOptions';

export type SuburbAccordionItemProps = {
  suburbs: SuburbType[];
};

export default function SuburbAccordionItem({ suburbs }: SuburbAccordionItemProps) {
  const [selectedSuburbs, setSuburbs] = useSuburbs(suburbs);
  const [search, setSearch] = useState('');

  const selectedIds = useMemo(
    () => new Set(selectedSuburbs.map((suburbItem) => suburbItem.id)),
    [selectedSuburbs]
  );
  const selectedCount = selectedIds.size;

  const filteredSuburbs = useMemo(
    () =>
      suburbs
        .filter((suburbItem) => suburbItem.menuTitle?.toLowerCase().includes(search.toLowerCase()))
        .sort((a, b) => a.menuTitle?.localeCompare(b.menuTitle ?? '') ?? 0),
    [suburbs, search]
  );
  const hasAdditionalSelection = filteredSuburbs
    .slice(COLLAPSED_FILTER_LIMIT)
    .some(({ id }) => selectedIds.has(id));

  const { expanded, toggleExpanded } = useExpandedFilterOptions(hasAdditionalSelection);

  const visibleSuburbs = filteredSuburbs.slice(0, expanded ? undefined : COLLAPSED_FILTER_LIMIT);

  const options = useMemo(
    () =>
      visibleSuburbs
        .filter((suburbItem) => suburbItem.id != null && suburbItem.menuTitle != null)
        .map((suburbItem) => ({
          value: `${suburbItem.id}`,
          label: suburbItem.menuTitle as string,
        })),
    [visibleSuburbs]
  );

  const values = useMemo(
    () => selectedSuburbs.map((suburbItem) => `${suburbItem.id}`),
    [selectedSuburbs]
  );

  return (
    <FilterAccordionItem
      value="suburb"
      title="Suburb"
      selectedCount={selectedCount}
      onClear={() => setSuburbs([])}
      expanded={expanded}
      onToggleExpanded={toggleExpanded}
      hasMore={filteredSuburbs.length > COLLAPSED_FILTER_LIMIT}>
      <div className="mt-2 flex flex-col gap-4">
        <div className="relative">
          <SearchIcon className="pointer-events-none absolute left-3.5 top-1/2 size-5 -translate-y-1/2 text-muted-foreground" />
          <Input
            scale="sm"
            placeholder="Search suburbs"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="h-12 w-full pl-11 text-sm focus-visible:ring-0"
          />
        </div>
        <div className="flex flex-col">
          {filteredSuburbs.length === 0 ? (
            <div className="py-3 text-sm text-gray">No results found</div>
          ) : (
            <CheckboxGroup
              options={options}
              values={values}
              setValues={(newValues) => {
                const nextIds = new Set(newValues);
                const nextSuburbs = suburbs.filter((suburbItem) => nextIds.has(`${suburbItem.id}`));
                setSuburbs(nextSuburbs);
              }}
            />
          )}
        </div>
      </div>
    </FilterAccordionItem>
  );
}
